import type { CardWithId } from '../../types/card';
import { Relationship } from './Relationship';
import { FamilyRelation } from './Family';
import { Position } from './GameMap';
import { POPULATION_CONSTANTS } from '../core/PopulationConstants';
import type { GroupId } from './Group';

/**
 * カードが禁忌かどうかをチェック
 * ExtendedCardData.isForbidden と CardData.forbidden の両方に対応
 */
export function isCardForbidden(card: CardWithId | { isForbidden?: boolean; forbidden?: boolean }): boolean {
  return !!(card as { isForbidden?: boolean }).isForbidden || !!(card as { forbidden?: boolean }).forbidden;
}

/**
 * 移動タイプ
 */
export type MovementType =
  | 'random' // ランダム移動
  | 'explorer' // 探索者（ランダムに遠くへ）
  | 'social' // 他NPCに近づく
  | 'avoider' // 他NPCを避ける
  // 属性シーカー（特定属性ゾーンを目指す）
  | 'fire_seeker' // 炎ゾーンを目指す
  | 'water_seeker' // 水ゾーンを目指す
  | 'light_seeker' // 光ゾーンを目指す
  | 'dark_seeker' // 闇ゾーンを目指す
  | 'primitive_seeker' // 原始ゾーンを目指す
  | 'future_seeker'; // 未来ゾーンを目指す

/**
 * 属性シーカータイプ一覧
 */
export const ATTRIBUTE_SEEKER_TYPES: MovementType[] = [
  'fire_seeker',
  'water_seeker',
  'light_seeker',
  'dark_seeker',
  'primitive_seeker',
  'future_seeker',
];

/**
 * 移動タイプから目標タイルタイプを取得
 */
export function getTargetTileForSeeker(
  movementType: MovementType
): string | null {
  switch (movementType) {
    case 'fire_seeker':
      return 'fire_zone';
    case 'water_seeker':
      return 'water_zone';
    case 'light_seeker':
      return 'light_zone';
    case 'dark_seeker':
      return 'dark_zone';
    case 'primitive_seeker':
      return 'primitive_zone';
    case 'future_seeker':
      return 'future_zone';
    default:
      return null;
  }
}

/**
 * カード別勝率統計
 */
export interface CardStat {
  gamesWithCard: number; // このカードを実際に使用した試合数（召喚・発動・設置）
  winsWithCard: number; // このカードを使用して勝った回数
  gamesInDeck: number; // このカードがデッキにある状態で戦った試合数
}

/**
 * シミュレーション用NPC
 * adventure/, collection/等のNPCとは完全に独立
 */
export interface SimNPC {
  // 基本情報
  id: string;
  name: string;
  age: number; // 日数

  // デッキ
  deck: CardWithId[];

  // ステータス
  health: number; // 0-100（体力）
  rating: number; // Eloレーティング（初期1000）

  // 対戦履歴
  recentOpponents: string[]; // 直近N日の対戦相手ID（古い順）

  // 統計
  wins: number;
  losses: number;
  daysLived: number;

  // 状態
  isAlive: boolean;

  // 関係性（Phase 1で追加）
  relationships: Map<string, Relationship>;

  // 家族関係（Phase 1で追加）
  family: FamilyRelation[];
  hasPartner: boolean;
  childrenCount: number; // 産んだ子供の数

  // 寿命（Phase 3で追加）
  maxLifespan: number; // 最大寿命（日数）

  // 位置情報（Phase 2で追加）
  position: Position;
  moveRange: number; // 移動範囲（1日あたりのマス数）
  movementType: MovementType;

  // カード別勝率統計（人口制御システムで追加）
  cardStats: Map<string, CardStat>; // cardId -> 統計

  // === 非対称進化モード用（淘汰判定用記録） ===
  /** 淘汰判定用勝利数（記録対象戦のみ） */
  recordedWins: number;
  /** 淘汰判定用敗北数（記録対象戦のみ） */
  recordedLosses: number;
  /** 淘汰判定用カード統計（記録対象戦のみ） */
  recordedCardStats: Map<string, CardStat>;

  // 世代管理
  generation: number; // 第何世代か（1から開始）
  birthDay: number; // 生まれた日（初期NPCは0）

  // グループ（グループモード時のみ使用）
  group?: GroupId; // 所属グループ（undefined = グループなしモード）
}

/**
 * SimNPC作成オプション
 */
export interface CreateSimNPCOptions {
  position?: Position;
  generation?: number; // デフォルト1（初期NPC）
  birthDay?: number; // デフォルト0
  group?: GroupId; // 所属グループ（グループモード時）
  maxLifespan?: number; // 寿命を指定（リスポーン時など）
}

/**
 * SimNPC作成
 */
export function createSimNPC(
  name: string,
  deck: CardWithId[],
  options: CreateSimNPCOptions = {}
): SimNPC {
  const { position, generation = 1, birthDay = 0, group, maxLifespan: customLifespan } = options;
  // 寿命はLIFESPAN_MIN〜LIFESPAN_MAX日のランダム（カスタム寿命指定がない場合）
  const { LIFESPAN_MIN, LIFESPAN_MAX } = POPULATION_CONSTANTS;
  const maxLifespan = customLifespan !== undefined
    ? customLifespan
    : Math.floor(LIFESPAN_MIN + Math.random() * (LIFESPAN_MAX - LIFESPAN_MIN));

  return {
    id: `npc_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
    name,
    age: 0,
    deck,
    health: 100,
    rating: 1000, // Eloレーティング初期値
    recentOpponents: [], // 直近対戦履歴
    wins: 0,
    losses: 0,
    daysLived: 0,
    isAlive: true,
    relationships: new Map(), // Phase 1で追加
    family: [], // Phase 1で追加
    hasPartner: false, // Phase 1で追加
    childrenCount: 0, // Phase 3で追加
    maxLifespan, // Phase 3で追加（定数で定義）
    // Phase 2で追加
    position: position || { x: 0, y: 0 }, // デフォルトは(0,0)
    moveRange: 1, // 1日1マス移動
    movementType: assignRandomMovementType(),
    // 人口制御システムで追加
    cardStats: new Map(),
    // 非対称進化モード用
    recordedWins: 0,
    recordedLosses: 0,
    recordedCardStats: new Map(),
    // 世代管理
    generation,
    birthDay,
    // グループモード
    group,
  };
}

/**
 * ランダムな移動タイプを割り当て
 * 基本4タイプ + 属性シーカー6タイプ = 10タイプ
 */
function assignRandomMovementType(): MovementType {
  const types: MovementType[] = [
    'random',
    'explorer',
    'social',
    'avoider',
    'fire_seeker',
    'water_seeker',
    'light_seeker',
    'dark_seeker',
    'primitive_seeker',
    'future_seeker',
  ];
  return types[Math.floor(Math.random() * types.length)];
}
